// Planned workouts. The planner gives each split a template (exercises with sets × reps and a weight), and can
// give any single day its own list instead. A day's planned exercises are worked out from that: template weights
// for weighted exercises move with the person's history (linear progression, see targets.ts); a day's own list
// is used exactly as written.
import { z } from 'zod';
import { Units } from './auth.ts';
import { describeNext, nextTarget } from './targets.ts';
import type { ExerciseType } from './training.ts';
import { convertWeight, roundWeight } from './units.ts';
import type { SetRecord } from './workouts.ts';

/** One exercise in a plan. Which numbers matter depends on the exercise type (weight × reps, reps, a hold, cardio). */
export const PlanEntry = z.object({
  exerciseId: z.string().min(1).max(100),
  sets: z.number().int().min(1, 'At least 1 set').max(20),
  reps: z.number().int().min(1, 'At least 1 rep').max(100).nullable(),
  /** Holds: seconds per set. Cardio: the session's length. */
  durationS: z.number().int().min(1).max(86_400).nullable(),
  distanceM: z.number().min(0).max(1_000_000).nullable(),
  /** In `unit`. In a template, the starting weight; in a day's own plan, that day's weight. */
  weight: z.number().min(0).max(2000).nullable(),
  unit: Units,
  /** Templates, weighted exercises: added after a session where every planned rep was hit. Null or 0: stays put. */
  increment: z.number().min(0).max(50).nullable(),
});
export type PlanEntry = z.infer<typeof PlanEntry>;
export const PlanEntries = z.array(PlanEntry).max(30);

/** What a day's plan says for one exercise, in the person's units. */
export const PlannedExercise = z.object({
  exerciseId: z.string(),
  sets: z.number().int(),
  reps: z.number().int().nullable(),
  durationS: z.number().int().nullable(),
  distanceM: z.number().nullable(),
  weight: z.number().nullable(),
  /** Where the weight comes from: progression from history (start/up/same/deload), the template as written, or set for this day. */
  basis: z.enum(['start', 'up', 'same', 'deload', 'template', 'day']),
  /** Last session's working weight, when the weight comes from progression. */
  last: z.number().nullable(),
  /** How much it goes up once this plan is hit (template progression only). */
  increment: z.number().nullable(),
});
export type PlannedExercise = z.infer<typeof PlannedExercise>;

type History = { lastSets: SetRecord[]; prevSets: SetRecord[] };

/** The planned exercises for a day, from either the split's template or the day's own list. */
export function plannedExercises(
  entries: PlanEntry[],
  source: 'template' | 'day',
  types: Map<string, ExerciseType>,
  history: Map<string, History>,
  units: Units,
): PlannedExercise[] {
  return entries.map((e) => {
    const base = { exerciseId: e.exerciseId, sets: e.sets, reps: e.reps, durationS: e.durationS, distanceM: e.distanceM };
    const fixed = e.weight === null ? null : roundWeight(convertWeight(e.weight, e.unit, units), units);
    const progresses = source === 'template' && types.get(e.exerciseId) === 'weight_reps' && e.reps !== null && (e.increment ?? 0) > 0;
    if (!progresses) return { ...base, weight: fixed, basis: source, last: null, increment: null };
    const h = history.get(e.exerciseId);
    const next = nextTarget(
      { exerciseId: e.exerciseId, sets: e.sets, reps: e.reps!, increment: e.increment!, unit: e.unit, startWeight: e.weight },
      h?.lastSets ?? [],
      h?.prevSets ?? [],
      units,
    );
    return { ...base, weight: next.weight, basis: next.basis, last: next.last, increment: roundWeight(convertWeight(e.increment!, e.unit, units), units) };
  });
}

/** Why the planned weight is what it is, in a sentence (or '' when there's nothing to say). */
export function describePlanned(p: PlannedExercise, units: Units): string {
  if (p.basis === 'day') return 'Set for this day.';
  if (p.basis === 'template') return '';
  return describeNext({ sets: p.sets, reps: p.reps ?? 0, weight: p.weight, basis: p.basis, last: p.last }, units);
}

/** "3 × 8 @ 95 lb", "3 × 12", "3 × 0:45", "20:00 · 3 mi". */
export function planText(p: Pick<PlannedExercise, 'sets' | 'reps' | 'durationS' | 'distanceM' | 'weight'>, type: ExerciseType, units: Units): string {
  const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  switch (type) {
    case 'weight_reps':
      return `${p.sets} × ${p.reps ?? '?'}${p.weight !== null ? ` @ ${p.weight} ${units}` : ''}`;
    case 'bodyweight_reps':
      return `${p.sets} × ${p.reps ?? '?'}`;
    case 'hold':
      return `${p.sets} × ${p.durationS !== null ? mmss(p.durationS) : '?'}`;
    case 'cardio': {
      const parts = [
        p.durationS !== null ? mmss(p.durationS) : null,
        p.distanceM !== null ? `${+(p.distanceM / (units === 'lb' ? 1609.344 : 1000)).toFixed(2)} ${units === 'lb' ? 'mi' : 'km'}` : null,
      ];
      return parts.filter(Boolean).join(' · ') || `${p.sets} session${p.sets === 1 ? '' : 's'}`;
    }
  }
}

/**
 * How far through the plan a workout is: sets that reached the planned reps (or time), at the planned weight or
 * more for weighted exercises. Warm-ups and deleted sets don't count.
 */
export function planProgress(p: PlannedExercise, type: ExerciseType, sets: Pick<SetRecord, 'weight' | 'unit' | 'reps' | 'durationS' | 'warmup' | 'deletedAt'>[], units: Units) {
  const working = sets.filter((s) => !s.warmup && !s.deletedAt);
  const meets = (s: (typeof working)[number]) => {
    if (type === 'weight_reps') return (s.reps ?? 0) >= (p.reps ?? 0) && (p.weight === null || (s.weight !== null && convertWeight(s.weight, s.unit, units) >= p.weight - 0.01));
    if (type === 'bodyweight_reps') return (s.reps ?? 0) >= (p.reps ?? 0);
    if (type === 'hold') return (s.durationS ?? 0) >= (p.durationS ?? 0);
    return true;
  };
  const done = working.filter(meets).length;
  return { done: Math.min(done, p.sets), hit: done >= p.sets };
}
